import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Plus } from 'lucide-react';

export function CreditManagement() {
    const [email, setEmail] = useState('');
    const [amount, setAmount] = useState('');
    const [creditType, setCreditType] = useState('admin');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();
    const { session } = useAuth();

    const handleAddCredits = async () => {
        if (!email || !amount) {
            toast({
                variant: 'destructive',
                title: 'Erro',
                description: 'Preencha email e quantidade'
            });
            return;
        }

        const creditAmount = parseInt(amount);
        if (isNaN(creditAmount) || creditAmount === 0) {
            toast({
                variant: 'destructive',
                title: 'Erro',
                description: 'Quantidade inválida'
            });
            return;
        }

        setLoading(true);
        try {
            // Use admin-actions edge function to search user (bypasses RLS, uses service role)
            const { data: searchData, error: searchError } = await supabase.functions.invoke("admin-actions", {
                body: { action: "search_user", email: email.trim() },
                headers: { Authorization: `Bearer ${session?.access_token}` },
            });

            if (searchError || searchData?.error) {
                throw new Error(searchData?.error || searchError?.message || 'Falha ao buscar usuário');
            }

            const profiles = searchData?.profiles || [];
            if (profiles.length === 0) {
                toast({
                    variant: 'destructive',
                    title: 'Erro',
                    description: 'Usuário não encontrado'
                });
                setLoading(false);
                return;
            }

            const profileId = profiles[0].id;

            // Use admin-actions to adjust credits
            const { data: adjustData, error: adjustError } = await supabase.functions.invoke("admin-actions", {
                body: {
                    action: "adjust_credits",
                    userId: profileId,
                    amount: creditAmount,
                    reason: description || 'Ajuste manual de créditos pelo admin',
                },
                headers: { Authorization: `Bearer ${session?.access_token}` },
            });

            if (adjustError || adjustData?.error) {
                throw new Error(adjustData?.error || adjustError?.message || 'Falha ao ajustar créditos');
            }

            toast({
                title: 'Sucesso ✓',
                description: `${creditAmount} créditos ${creditAmount > 0 ? 'adicionados' : 'removidos'} para ${email}`
            });

            // Clear form
            setEmail('');
            setAmount('');
            setDescription('');
        } catch (error: any) {
            console.error('[CreditManagement] Error:', error);
            toast({
                variant: 'destructive',
                title: 'Erro ao processar',
                description: error.message || 'Falha ao adicionar créditos'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Gerenciar Créditos
                </CardTitle>
                <CardDescription>
                    Adicione ou remova créditos de usuários manualmente
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="user-email">Email do Usuário</Label>
                    <Input
                        id="user-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="usuario@example.com"
                        disabled={loading}
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="credit-amount">
                        Quantidade
                        <span className="text-xs text-muted-foreground ml-2">
                            (use valor negativo para remover)
                        </span>
                    </Label>
                    <Input
                        id="credit-amount"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="10"
                        disabled={loading}
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="credit-type">Tipo de Crédito</Label>
                    <Select value={creditType} onValueChange={setCreditType} disabled={loading}>
                        <SelectTrigger id="credit-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="admin">Admin (ajuste manual)</SelectItem>
                            <SelectItem value="referral">Indicação</SelectItem>
                            <SelectItem value="purchase">Compra</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="description">Descrição (opcional)</Label>
                    <Textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Motivo do ajuste..."
                        disabled={loading}
                        rows={3}
                    />
                </div>

                <Button
                    onClick={handleAddCredits}
                    disabled={loading}
                    className="w-full"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processando...
                        </>
                    ) : (
                        <>
                            <Plus className="w-4 h-4 mr-2" />
                            Adicionar Créditos
                        </>
                    )}
                </Button>
            </CardContent>
        </Card>
    );
}
